import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Gift, Copy, Share2, UserPlus, CreditCard, CheckCircle2, XCircle, Wallet,
  Clock, HelpCircle, ArrowRight, Sparkles, Info,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

function Section({ icon: Icon, title, children }) {
  return (
    <motion.section
      variants={fade}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '-60px' }}
      className="bg-white rounded-3xl border border-gray-100 shadow-[0_16px_50px_-24px_rgba(0,150,177,0.25)] p-6 md:p-8"
    >
      <h2 className="flex items-center gap-3 text-xl md:text-2xl font-bold text-[#2b5356] mb-4">
        <span className="grid place-items-center w-11 h-11 rounded-2xl bg-gradient-to-br from-[#0096b1]/10 to-[#2b5356]/10 text-[#0096b1]">
          <Icon className="w-6 h-6" />
        </span>
        {title}
      </h2>
      {children}
    </motion.section>
  );
}

const STEPS = [
  { icon: Copy, t: 'Sao chép Link Affiliate', d: 'Vào Trang cá nhân → Affiliate → bấm Copy Link.' },
  { icon: Share2, t: 'Chia sẻ link', d: 'Gửi cho bạn bè / học viên qua Facebook, Zalo, Messenger, Email, Website, Blog, YouTube, TikTok…' },
  { icon: UserPlus, t: 'Người được giới thiệu mở link', d: 'Họ vào website qua link của bạn và đăng ký tài khoản mới.' },
  { icon: CreditCard, t: 'Mua VIP & thanh toán', d: 'Khi họ mua gói VIP thành công, hệ thống tự động cộng 10% hoa hồng vào ví của bạn.' },
];

const TIERS = [
  ['200.000 VNĐ', '20.000 xu'],
  ['300.000 VNĐ', '30.000 xu'],
  ['500.000 VNĐ', '50.000 xu'],
  ['1.000.000 VNĐ', '100.000 xu'],
];

const FAQ = [
  ['Tôi có cần đăng ký để tham gia Affiliate không?', 'Không. Mỗi tài khoản Customer đều tự động có Link Affiliate.'],
  ['Tôi có thể giới thiệu bao nhiêu người?', 'Không giới hạn.'],
  ['Khi nào tôi nhận được hoa hồng?', 'Ngay sau khi đơn hàng của người được giới thiệu thanh toán thành công và đáp ứng điều kiện.'],
  ['Tôi có thể dùng số dư ví để mua VIP không?', 'Không. Ví Affiliate chỉ dùng để tích lũy và rút tiền.'],
  ['Khi nào tôi được rút tiền?', 'Khi số dư ví đạt từ 300.000 xu trở lên.'],
  ['Tôi sẽ nhận tiền bằng cách nào?', 'Sau khi gửi yêu cầu và được Admin duyệt, tiền được chuyển khoản vào tài khoản ngân hàng bạn cung cấp.'],
  ['Tôi xem lịch sử hoa hồng ở đâu?', 'Trong Trang cá nhân → Affiliate → Lịch sử ví.'],
];

export default function AffiliateGuide() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0096b1] via-[#0d8ba3] to-[#2b5356] text-white">
          <div className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-[#eb7e37]/20 blur-3xl" />
          <div className="relative max-w-4xl mx-auto px-4 py-16 text-center">
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }}
              className="inline-grid place-items-center w-16 h-16 rounded-3xl bg-white/15 backdrop-blur ring-1 ring-white/20 mb-4">
              <Gift className="w-8 h-8" />
            </motion.div>
            <h1 className="text-3xl md:text-4xl font-extrabold">Hướng dẫn Affiliate</h1>
            <p className="mt-3 text-white/85 max-w-2xl mx-auto">Chia sẻ link, người được giới thiệu mua VIP — bạn nhận ngay <b>10% hoa hồng</b> trên giá trị đơn (1 xu = 1 VNĐ).</p>
            <Link to="/profile?tab=affiliate" className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-xl bg-white text-[#0096b1] font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
              Vào mục Affiliate của tôi <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 py-10 space-y-6">
          <Section icon={Sparkles} title="Affiliate là gì?">
            <p className="text-gray-600 leading-relaxed">Affiliate là chương trình giới thiệu khách hàng của website. Khi bạn chia sẻ <b>Link Affiliate</b> và người khác đăng ký rồi mua gói VIP qua link đó, bạn nhận <b>10% hoa hồng</b> trên giá trị thực tế của đơn.</p>
            <div className="mt-4 rounded-2xl bg-[#0096b1]/5 border border-[#0096b1]/15 p-4 text-sm text-gray-700">
              <div className="font-semibold text-[#2b5356] mb-1">Ví dụ</div>
              Bạn bè đăng ký qua link của bạn → mua gói VIP <b>250.000 VNĐ</b> → bạn nhận <b>25.000 xu</b>.
            </div>
            <div className="mt-4 flex items-start gap-2.5 rounded-2xl bg-amber-50 border border-amber-100 p-4 text-sm text-gray-700">
              <Info className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <p><b className="text-amber-700">Lưu ý:</b> Chỉ cần một tài khoản được đăng ký thông qua Link Affiliate của bạn, bạn sẽ <b>nhận hoa hồng cho mọi lần mua hoặc gia hạn VIP</b> thành công của tài khoản đó, kể cả các lần mua sau này.</p>
            </div>
          </Section>

          <Section icon={Copy} title="Cách tham gia">
            <p className="text-gray-600 mb-3">Mỗi tài khoản Customer đều được cấp <b>một Link Affiliate duy nhất</b> ngay khi đăng ký. Vào <b>Trang cá nhân → Affiliate</b>, bạn sẽ thấy:</p>
            <ul className="grid sm:grid-cols-2 gap-2 text-sm text-gray-600">
              {['Link Affiliate của mình + nút Copy', 'Số lượt đăng ký qua link', 'Tổng hoa hồng đã nhận', 'Số dư ví (xu)', 'Lịch sử ví'].map(x => (
                <li key={x} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> {x}</li>
              ))}
            </ul>
          </Section>

          <Section icon={Share2} title="Cách hoạt động">
            <div className="grid sm:grid-cols-2 gap-3">
              {STEPS.map((s, i) => (
                <div key={i} className="relative rounded-2xl border border-gray-100 p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="grid place-items-center w-7 h-7 rounded-full bg-[#0096b1] text-white text-sm font-bold">{i + 1}</span>
                    <s.icon className="w-5 h-5 text-[#0096b1]" />
                    <span className="font-semibold text-gray-800">{s.t}</span>
                  </div>
                  <p className="text-sm text-gray-500">{s.d}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section icon={CheckCircle2} title="Điều kiện nhận hoa hồng">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
                <div className="font-semibold text-emerald-700 mb-2">Được tính khi</div>
                <ul className="space-y-1.5 text-sm text-gray-700">
                  {['Người mua vào website qua Link Affiliate của bạn', 'Thanh toán gói VIP thành công', 'Đơn không bị hủy', 'Đơn không được hoàn tiền'].map(x => (
                    <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {x}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl bg-red-50 border border-red-100 p-4">
                <div className="font-semibold text-red-600 mb-2">Không được tính khi</div>
                <ul className="space-y-1.5 text-sm text-gray-700">
                  {['Thanh toán thất bại', 'Đơn hàng bị hủy', 'Đơn hàng được hoàn tiền'].map(x => (
                    <li key={x} className="flex items-start gap-2"><XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" /> {x}</li>
                  ))}
                </ul>
              </div>
            </div>
          </Section>

          <Section icon={Wallet} title="Hoa hồng & Ví">
            <p className="text-gray-600 mb-3">Hoa hồng = <b>10%</b> số tiền thực tế khách thanh toán, cộng thẳng vào <b>Ví Affiliate</b> (chỉ để tích lũy + rút tiền, <b>không</b> dùng mua VIP).</p>
            <div className="overflow-x-auto rounded-2xl border border-gray-100">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr><th className="text-left px-4 py-2.5">Giá trị gói VIP</th><th className="text-right px-4 py-2.5">Hoa hồng</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {TIERS.map(([a, b]) => (
                    <tr key={a}><td className="px-4 py-2.5 text-gray-700">{a}</td><td className="px-4 py-2.5 text-right font-semibold text-[#0096b1]">{b}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-2">1 xu = 1 VNĐ. Mọi biến động ví đều được lưu trong Lịch sử ví.</p>
          </Section>

          <Section icon={Clock} title="Rút tiền & thời gian xử lý">
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2"><ArrowRight className="w-4 h-4 text-[#0096b1] shrink-0 mt-0.5" /> Gửi yêu cầu rút khi số dư <b className="mx-1">≥ 300.000 xu</b>, kèm thông tin nhận tiền (QR hoặc ngân hàng: chủ TK, số TK).</li>
              <li className="flex items-start gap-2"><ArrowRight className="w-4 h-4 text-[#0096b1] shrink-0 mt-0.5" /> Sau khi gửi: hệ thống trừ ví ngay, request chuyển sang <b className="mx-1">Chờ xử lý</b>.</li>
              <li className="flex items-start gap-2"><ArrowRight className="w-4 h-4 text-[#0096b1] shrink-0 mt-0.5" /> Admin kiểm tra & chuyển khoản trong vòng <b className="mx-1">30 ngày</b>. Xong sẽ chuyển sang Lịch sử thanh toán.</li>
              <li className="flex items-start gap-2"><ArrowRight className="w-4 h-4 text-[#0096b1] shrink-0 mt-0.5" /> Nếu Admin từ chối, số tiền được <b className="mx-1">hoàn lại</b> vào ví.</li>
            </ul>
          </Section>

          <Section icon={Info} title="Một số lưu ý">
            <ul className="grid sm:grid-cols-2 gap-2 text-sm text-gray-600">
              {['Mỗi tài khoản chỉ có một Link Affiliate duy nhất.', 'Link tạo tự động khi đăng ký; không cần đăng ký riêng.', 'Chỉ đơn thanh toán thành công mới được ghi nhận.', 'Hoa hồng tính hoàn toàn tự động.', 'Không giới hạn số người bạn giới thiệu.', 'Theo dõi số liệu & lịch sử ngay trong mục Affiliate.'].map(x => (
                <li key={x} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#0096b1] shrink-0 mt-0.5" /> {x}</li>
              ))}
            </ul>
          </Section>

          <Section icon={HelpCircle} title="Câu hỏi thường gặp">
            <div className="space-y-3">
              {FAQ.map(([q, a]) => (
                <div key={q} className="rounded-2xl border border-gray-100 p-4">
                  <div className="font-semibold text-gray-800">{q}</div>
                  <div className="text-sm text-gray-500 mt-1">{a}</div>
                </div>
              ))}
            </div>
          </Section>

          <div className="text-center pt-2">
            <Link to="/profile?tab=affiliate" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#0096b1] to-[#2b5356] text-white font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
              Bắt đầu kiếm hoa hồng <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
